<template>
    <form class="search-filters" @submit.prevent="searchSubmit()" :class="{'search-filters--disabled': aggs.updating }">
        <div class="search">
            <label class="search-filters__control" for="location" v-if="!predefinedFilters.location && !predefinedFilters.property">
                Location
                <input type="text" name="filter_location" id="location" v-model="params.filter_location"
                    list="locations" autocomplete="off" placeholder="Search locations" @change="changedFilter('filter_location')"
                    :disabled="aggs.updating"
                    />
                <datalist id="locations">
                    <option v-for="location in locationNames" :value="location"
                        :disabled="location.count == 0"></option>
                </datalist>
            </label>

            <label class="search-filters__control" for="university" v-if="!predefinedFilters.property">
                University
                <input type="text" name="filter_university" id="university" v-model="params.filter_university"
                    list="universities" autocomplete="off" placeholder="Search universities" @change="changedFilter('filter_university')"
                    :disabled="aggs.updating"
                    />
                <datalist id="universities">
                    <option v-for="university in aggs.filters.universities" :value="university.name"
                        :disabled="university.count == 0"></option>
                </datalist>
            </label>

            <label class="search-filters__control" for="property" v-if="!predefinedFilters.property">
                Property
                <select name="filter_property" id="property" v-model="params.filter_property" @change="changedFilter('filter_property')"
                :disabled="aggs.updating">
                    <option value="">All</option>
                    <option v-for="property in aggs.filters.properties" :value="property.name"
                        :disabled="property.count == 0">
                        {{ property.name }} ({{ property.count }})
                    </option>
                </select>
            </label>

            <label class="search-filters__control" for="academic_year_offset">
                <span class="search-filters__label-text screen-reader-text">Academic Year</span>
                <select class="search-filters__select" name="academic_year_offset" id="academic_year_offset" v-model="params.academic_year_offset" @change="changedFilter('academic_year_offset')"
                :disabled="aggs.updating">
                    <option value="0">{{ currentAcademicYear ?? 'Current Year' }}</option>
                    <option value="1">{{ nextAcademicYear ?? 'Next Year' }}</option>
                </select>
            </label>

            <label for="show_available" class="search-filters__control search-filters__control--checkbox">
                <span class="search-filters__label-text">Show available rooms only</span>
                <input type="checkbox" name="show_available" id="show_available" value="0"
                    v-model="params.show_available" :disabled="aggs.updating"
                    @change="changedFilter('show_available')" />
            </label>

            <label class="search-filters__control" for="room_type">
                <span class="search-filters__label-text screen-reader-text">Room Type</span>
                <select class="search-filters__select" name="filter_room_type" id="room_type" v-model="params.filter_room_type" @change="changedFilter('filter_room_type')"
                :disabled="aggs.updating">
                    <option value="">All Room Types</option>
                    <option v-for="room_type in aggs.filters.room_types" :value="room_type.name"
                        :disabled="room_type.count == 0">
                        {{ room_type.name }} ({{ room_type.count }})
                    </option>
                </select>
            </label>

            <label class="search-filters__control" for="sort" v-if="searchMode == 'full' || searchMode == 'alternative'">
                <span class="search-filters__label-text screen-reader-text">Sort</span>
                <select class="search-filters__select" name="sort" id="sort" v-model="params.sort" @change="changedFilter('sort')">
                    <option value="az">Sort by A-Z</option>
                    <option value="za">Sort by Z-A</option>
                    <option value="priceasc">Sort by Lowest price</option>
                    <option value="pricedesc">Sort By Highest price</option>
                </select>
            </label>
        </div>
    </form>
</template>
<script setup>
import { toRef, onMounted } from 'vue';

const props = defineProps({
    // The current academic year range as a string, e.g. 2023/24
    currentAcademicYear: {
        type: String,
        required: true
    },
    // The next academic year range as a string, e.g. 2024/25
    nextAcademicYear: {
        type: String,
        required: true
    },
    // The search params object - this is what contains our current filter
    // settings and is mutated by the form
    searchParams: {
        type: Object,
        required: true
    },
    // The aggregations object - this is what contains the potential result 
    // counts for each filter option
    aggregations: {
        type: Object,
        required: true
    },
    // The location names array
    locationNames: {
        type: Array,
        default: () => []
    },
    // The university names array
    universityNames: {
        type: Array,
        default: () => []
    },
    // The search mode - either 'full' or 'home'
    searchMode: {
        type: String,
        default: 'full'
    },
    // The predefined filters object
    predefinedFilters: {
        type: Object,
        default: () => {}
    }
});

const params = toRef(props, 'searchParams');
const aggs = toRef(props, 'aggregations');

const emit = defineEmits(['submit', 'changed-filter']);

// Submit the search form
const searchSubmit = () => {
    emit('submit');
};

const changedFilter = (filter) => {
    emit('changed-filter', {
        name: filter,
        value: props.searchParams[filter]
    });
    searchSubmit();
};

onMounted(() => {
    if (props.predefinedFilters) {
        Object.keys(props.predefinedFilters).forEach((filter) => {
            props.searchParams['filter_' + filter] = props.predefinedFilters[filter];
        });
        changedFilter();
    }
})

</script>